import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MoreHorizontal, UploadCloud, PlayCircle } from 'lucide-react';

// Mock data for models
const mockModels = [
  {
    id: 'model-1',
    name: 'Finetuned-Llama3-8B',
    version: 'v1.2',
    status: 'ACTIVE',
  },
  {
    id: 'model-2',
    name: 'Base-Mistral-7B',
    version: 'v1.0',
    status: 'INACTIVE',
  },
  {
    id: 'model-3',
    name: 'Finetuned-Llama3-8B',
    version: 'v1.1',
    status: 'UPLOADED',
  },
];

const ModelManagementPage: React.FC = () => {
  const [isUploadModalOpen, setIsUploadModalOpen] = React.useState(false);
  const [testResponse, setTestResponse] = React.useState('');
  const [isTesting, setIsTesting] = React.useState(false);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge className="bg-green-500">Active</Badge>;
      case 'INACTIVE':
        return <Badge variant="secondary">Inactive</Badge>;
      case 'UPLOADED':
        return <Badge variant="outline">Uploaded</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };
  
  const handleTest = () => {
    setIsTesting(true);
    setTestResponse('');
    setTimeout(() => {
        setTestResponse('The total revenues for Q3 2023 were $15.2 million, a 12% increase year-over-year.');
        setIsTesting(false);
    }, 1500);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Model Management</h1>
        <Button onClick={() => setIsUploadModalOpen(true)}>
          <UploadCloud className="mr-2 h-4 w-4" />
          Upload New Model
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Uploaded Models</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Model Name</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mockModels.map((model) => (
                <TableRow key={model.id}>
                  <TableCell className="font-medium">{model.name}</TableCell>
                  <TableCell>{model.version}</TableCell>
                  <TableCell>{getStatusBadge(model.status)}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {model.status !== 'ACTIVE' && <DropdownMenuItem>Activate for Testing</DropdownMenuItem>}
                        {model.status === 'ACTIVE' && <DropdownMenuItem>Deactivate</DropdownMenuItem>}
                        <DropdownMenuItem className="text-red-500">Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Test Active Models</CardTitle>
          <CardDescription>Run a quick inference test on any active model.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
            <div className="space-y-2">
                <Label>Select Model</Label>
                <Select>
                    <SelectTrigger>
                        <SelectValue placeholder="Select an active model" />
                    </SelectTrigger>
                    <SelectContent>
                        {mockModels.filter(m => m.status === 'ACTIVE').map(m => (
                            <SelectItem key={m.id} value={m.id}>{m.name} ({m.version})</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="space-y-2">
                <Label htmlFor="prompt">Prompt</Label>
                <Textarea id="prompt" placeholder="Enter your prompt here..." />
            </div>
            <Button onClick={handleTest} disabled={isTesting}>
                <PlayCircle className="mr-2 h-4 w-4" />
                {isTesting ? 'Running...' : 'Run Inference'}
            </Button>
            {testResponse && (
                <div className="space-y-2">
                    <Label>Response</Label>
                    <div className="p-4 border rounded-md bg-muted text-sm">
                        {testResponse}
                    </div>
                </div>
            )}
        </CardContent>
      </Card>

      <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload New Model</DialogTitle>
            <DialogDescription>Provide model details and upload the file.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
                <Label htmlFor="model-name">Model Name</Label>
                <Input id="model-name" placeholder="e.g., Finetuned-Llama3-8B" />
            </div>
            <div className="space-y-2">
                <Label htmlFor="model-version">Version</Label>
                <Input id="model-version" placeholder="e.g., v1.2" />
            </div>
            <div className="space-y-2">
                <Label htmlFor="model-description">Description</Label>
                <Textarea id="model-description" placeholder="Describe the model..." />
            </div>
            <div className="space-y-2">
                <Label htmlFor="model-file">Model File</Label>
                <Input id="model-file" type="file" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUploadModalOpen(false)}>Cancel</Button>
            <Button onClick={() => setIsUploadModalOpen(false)}>Upload</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModelManagementPage;
